// frontend/src/pages/model/modelLogic.js
// Phase 3 step 13b: the rules behind the Model page, pure and unit-tested (no React).
//
// The server (app/semantic/model.py) is the authority on what a change may be;
// these rules only keep the page from offering changes it would refuse, and they
// offer exactly what the builder offers for a field, so a default set here is one
// the builder can use. "Automatic" is the choice that clears a setting (null).
import { AGGREGATION_LABELS, GRAINS, aggregationsFor, startingAggregation } from '../builder/builderSpec';

export const AUTO = 'auto';   // a select's value for "automatic" (Radix and native selects both dislike an empty value)

const NUMERIC = ['integer', 'number'];
const TEMPORAL = ['date', 'datetime'];
const STRUCTURED = ['json_array', 'json_object'];

export const ROLE_LABELS = { dimension: 'Dimension', measure: 'Measure', time: 'Time', identifier: 'Identifier', ignore: 'Ignore' };
export const GRAIN_LABELS = { day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter', year: 'Year' };
export const TYPE_LABELS = {
  string: 'Text', integer: 'Whole number', number: 'Number', boolean: 'Yes / no', date: 'Date', datetime: 'Date and time',
  json_array: 'List', json_object: 'Object',
};

export const SHOW_FILTERS = [['all', 'All'], ['visible', 'Visible'], ['hidden', 'Hidden'], ['used', 'In charts']];

export const MAX_LABEL = 255;          // mirrors MAX_DISPLAY_NAME in app/semantic/model.py
export const CHARTS_SHOWN = 10;        // charts listed in the "in use" dialog before "and N more"

export const isStructured = (field) => STRUCTURED.includes(field.data_type);
export const isTemporal = (field) => TEMPORAL.includes(field.data_type);
export const typeLabel = (field) => TYPE_LABELS[field.data_type] || String(field.data_type || '');

/** The role the field has before any override, as the builder would treat it. */
const baseRole = (field) => field.role_override || field.default_role;

// --- the three classification controls ------------------------------------

/** Roles the field may be given (the server's rules: a measure is a number, time is a date, json has none). */
export function roleChoices(field) {
  if (isStructured(field)) return [];
  const roles = ['dimension', 'measure', 'time', 'identifier', 'ignore'].filter((r) => (
    (r !== 'measure' || NUMERIC.includes(field.data_type)) && (r !== 'time' || isTemporal(field))
  ));
  return [
    { value: AUTO, label: `Automatic (${ROLE_LABELS[field.default_role] || field.default_role})` },
    ...roles.map((r) => ({ value: r, label: ROLE_LABELS[r] })),
  ];
}

export const roleValue = (field) => (field.role_override && roleChoices(field).some((c) => c.value === field.role_override) ? field.role_override : AUTO);

/**
 * What the field starts as when it becomes a value in a chart. Returns [] when
 * there is nothing to choose (text, yes/no and ids only count distinct values),
 * else Automatic first, then what the builder offers for this type.
 */
export function aggregationChoices(field) {
  const offered = isStructured(field) ? [] : aggregationsFor(field);
  if (offered.length < 2) return [];
  const auto = startingAggregation({ ...field, role: baseRole(field), default_aggregation: null });
  return [
    { value: AUTO, label: `Automatic (${AGGREGATION_LABELS[auto]})` },
    ...offered.map((a) => ({ value: a, label: AGGREGATION_LABELS[a] })),
  ];
}

/** The text shown when there is no choice to make: what the field does as a value, or nothing for json. */
export function aggregationFixedText(field) {
  if (isStructured(field)) return null;
  return AGGREGATION_LABELS[startingAggregation({ ...field, role: baseRole(field), default_aggregation: null })] || null;
}

/** The select's value. A stored default the builder does not offer for this type shows as Automatic (the builder ignores it too). */
export const aggregationValue = (field) => (
  field.default_aggregation && aggregationChoices(field).some((c) => c.value === field.default_aggregation) ? field.default_aggregation : AUTO
);

/** Dates only: the grain a date is grouped by when it is put on an axis. */
export function grainChoices(field) {
  if (!isTemporal(field)) return [];
  return [
    { value: AUTO, label: `Automatic (${GRAIN_LABELS.month})` },
    ...GRAINS.map((g) => ({ value: g, label: GRAIN_LABELS[g] })),
  ];
}

export const grainValue = (field) => (GRAINS.includes(field.default_grain) && isTemporal(field) ? field.default_grain : AUTO);

/** The change a control's choice means: Automatic clears the setting. `prop` is role, default_aggregation or default_grain. */
export function choicePatch(prop, value) {
  if (!['role', 'default_aggregation', 'default_grain'].includes(prop)) throw new Error(`not a classification setting: ${prop}`);
  return { [prop]: value === AUTO ? null : value };
}

// --- names -----------------------------------------------------------------

/** What typing `text` as a field's label means: null when nothing changes; empty goes back to the automatic label. */
export function labelPatch(field, text) {
  const next = String(text == null ? '' : text).trim();
  if (next === '') return field.display_name_override == null ? null : { display_name: null };
  if (next === field.display_name) return null;
  return { display_name: next };
}

/** The same for the dataset's name; empty goes back to the automatic name (the server decides what that is). */
export function namePatch(current, text) {
  const next = String(text == null ? '' : text).trim();
  if (next === String(current || '')) return null;
  return { display_name: next === '' ? null : next };
}

/** Key names "." and ".." cannot go in a URL path, so those fields cannot be changed from here. */
export const canEditField = (field) => field.key !== '' && field.key !== '.' && field.key !== '..';

// --- the list --------------------------------------------------------------

/** Fields matching a search (label or key) and a view: all, visible, hidden, or used by charts. */
export function filterFields(fields, { query = '', show = 'all' } = {}) {
  const q = String(query).trim().toLowerCase();
  return (fields || []).filter((f) => {
    if (show === 'visible' && f.hidden) return false;
    if (show === 'hidden' && !f.hidden) return false;
    if (show === 'used' && !(f.used_in_charts > 0)) return false;
    return !q || String(f.display_name || '').toLowerCase().includes(q) || String(f.key).toLowerCase().includes(q);
  });
}

export function countFields(fields) {
  const list = fields || [];
  return { total: list.length, hidden: list.filter((f) => f.hidden).length, used: list.filter((f) => f.used_in_charts > 0).length };
}

/** A changed field put back where it was (rows do not jump while someone is editing). */
export const replaceField = (fields, updated) => fields.map((f) => (f.key === updated.key ? updated : f));

export function usageLabel(field) {
  const n = field.used_in_charts || 0;
  return n === 0 ? 'Not used' : n === 1 ? '1 chart' : `${n} charts`;
}

/** "How full" a field is: the share of sampled records that have a value. */
export function filledLabel(field) {
  const ratio = Number(field.null_ratio);
  if (!Number.isFinite(ratio)) return '';
  return `${Math.round((1 - Math.min(1, Math.max(0, ratio))) * 100)}% filled`;
}

/** Where a chart opens, or null for an id that is not a positive integer. */
export const chartHref = (id) => (Number.isInteger(id) && id > 0 ? `/charts/${id}` : null);

// --- the "in use" confirmation ----------------------------------------------

/** What the patch that was refused would do to the field, for the dialog's wording. */
export function removalVerb(patch) {
  return patch && patch.role === 'ignore' ? 'ignoring' : 'hiding';
}

export function confirmLabel(patch) {
  return patch && patch.role === 'ignore' ? 'Ignore anyway' : 'Hide anyway';
}

export function inUseTitle(fieldName, count, calculations = 0) {
  const parts = [];
  if (count || !calculations) parts.push(count === 1 ? '1 chart' : `${count} charts`);
  if (calculations) parts.push(calculations === 1 ? '1 formula' : `${calculations} formulas`);   // 13c-3
  return `${fieldName} is used by ${parts.join(' and ')}`;
}

// --- errors ----------------------------------------------------------------

/** Words for a failed change: the typed server message when there is one, never raw error text. */
export function errorText(err) {
  if (err && err.name === 'SemanticError' && typeof err.message === 'string' && err.message) return err.message;
  return "Couldn't reach the server. Check your connection and try again.";
}

// --- the dataset header ----------------------------------------------------

/** "github" -> "Github", "google_sheets" -> "Google sheets": the source a dataset comes from. */
export function sourceName(key) {
  const k = String(key || 'other').replace(/_/g, ' ');
  return k.charAt(0).toUpperCase() + k.slice(1);
}

/** "Oct 5, 2026" for an ISO timestamp; '' when there is none or it is not a date. */
export function profiledLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Datasets grouped by source, sources and datasets in name order (the same grouping as the builder's dataset picker). */
export function groupBySource(datasets) {
  const groups = new Map();
  for (const d of datasets || []) {
    const key = d.source_key || 'other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(d);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({
      key,
      label: sourceName(key),
      items: [...items].sort((a, b) => String(a.display_name).localeCompare(String(b.display_name))),
    }));
}

/** A copy of an object without one key (state held as { [key]: value } maps). */
export function without(map, key) {
  const next = { ...map };
  delete next[key];
  return next;
}
